import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Highlight,
  IconBannersInfo,
  IconBannersWarning,
  IconDownload,
  LinkText,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import SearchableDropdown, {
  SearchableDropdownItem,
} from '../searchableDropdown/SearchableDropdown';
import DownloadModal from '../modal/DownloadModal';
import i18n from '../../utilities/i18n';
import DynamicTable, {
  ColumnConfig,
  CheckboxFilterSection,
} from '../table/DynamicTable';
import { useQuery } from '@tanstack/react-query';
import { studiesQueryOptions } from '../../queryOptions';
import { useAppSelector } from '../../reducers/hooks';
import { studyFullQueryOptions } from '../../queryOptions/study';
import { useNavigateWithBreadcrumbs, useStudyDownload } from '../../hooks';
import { DOWNLOAD_MODE, ROUTES } from '../../constants';
import log from '../../utilities/log';
import { EntityStatus } from 'adc-sirius-module-common';
import { enumToCheckboxOptions } from '../../utilities/helpers';

type DashboardRow = {
  subjectKey: string;
  subjectName: string;
  siteKey: string;
  siteName: string;
  status: EntityStatus;
  type: string;
  deviceIds: string[];
};

/**
 * Dashboard component that displays data for filter and download.
 * @returns {React.JSX.Element}
 */
export default function Dashboard() {
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSubject = (subjectKey: string, subjectLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SUBJECTS.DETAIL.replace(':subjectKey', subjectKey),
      subjectLabel,
      i18n.t('Page_Dashboard.title'),
    );
  };

  const navigateToSite = (siteKey: string, siteLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SITES.DETAIL.replace(':siteKey', siteKey),
      siteLabel,
      i18n.t('Page_Dashboard.title'),
    );
  };

  const capabilities = useAppSelector((state) => state.capabilities);

  const [dashboardData, setDashboardData] = useState<DashboardRow[]>([]);
  const [filteredData, setFilteredData] = useState<DashboardRow[]>([]);

  const [selectedStudyIndex, setSelectedStudyIndex] = useState(0);
  const [selectedSubjectKeys, setSelectedSubjectKeys] = useState<string[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const {
    downloadStudyData,
    isPending: isDownloading,
    downloadState,
    resetDownload,
  } = useStudyDownload();

  const { currentOrgKey } = useAppSelector((state) => state.organizations);

  const studies = useQuery(
    studiesQueryOptions({ organizationKey: currentOrgKey }),
  );

  const selectedStudyKey = studies.data?.[selectedStudyIndex]?.key;
  const selectedStudyName = studies.data?.[selectedStudyIndex]?.name;
  const studyData = useQuery(studyFullQueryOptions({ key: selectedStudyKey }));

  const [checkboxFilters, setCheckboxFilters] = useState<
    CheckboxFilterSection[]
  >([]);

  const initialCheckboxFilters = useMemo(
    () => [
      {
        id: 'subjectKey',
        label: i18n.t('Entity.subject'),
        items: (studyData.data?.subjects || []).map((subject) => ({
          id: subject.key,
          label: subject.name,
          value: false,
        })),
      },
      {
        id: 'siteKey',
        label: i18n.t('Entity.site'),
        items: (studyData.data?.sites || []).map((site) => ({
          id: site.key,
          label: site.name,
          value: false,
        })),
      },
      {
        id: 'status',
        label: i18n.t('Entity.status'),
        items: enumToCheckboxOptions(EntityStatus, 'Status'),
      },
    ],
    [studyData.data],
  );

  // initialize the selected study index when studies data is loaded
  useEffect(() => {
    setSelectedStudyIndex(0);
  }, [studies.data]);

  // initialize the checkbox filters based on the dashboard data cell values
  useEffect(() => {
    setCheckboxFilters(initialCheckboxFilters);
  }, [initialCheckboxFilters]);

  // process raw study data into unfiltered dashboard data table rows
  useEffect(() => {
    const processedData: DashboardRow[] = [];

    if (studyData.isSuccess && studyData.data?.subjects) {
      studyData.data.subjects.forEach((subject) => {
        const site = studyData.data?.sites.find(
          (site) => site.key === subject.siteKey,
        );

        // Get sensors for this subject
        const subjectSensors = studyData.data?.sensors?.get(subject.key) || [];

        // Collect unique device keys from sensors
        const uniqueDeviceKeys = Array.from(
          new Set(
            subjectSensors
              .map((sensor) => sensor.deviceKey)
              .filter((deviceKey) => deviceKey),
          ),
        );

        // Include subject's deviceId if it exists and isn't already in the list
        if (subject.deviceId && !uniqueDeviceKeys.includes(subject.deviceId)) {
          uniqueDeviceKeys.push(subject.deviceId);
        }

        processedData.push({
          subjectKey: subject.key,
          subjectName: subject.name,
          siteKey: subject.siteKey,
          siteName: site?.name || '',
          status: subject.status,
          type: subject.type,
          deviceIds: uniqueDeviceKeys,
        });
      });
    }

    setDashboardData(processedData);
  }, [studyData.data, studyData.isSuccess]);

  // apply filters to the dashboard data
  useEffect(() => {
    let filteredDataResult = [...dashboardData];

    checkboxFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0) {
        filteredDataResult = filteredDataResult.filter((dataItem) =>
          activeFilterValues.includes(
            String(dataItem[filter.id as keyof DashboardRow]),
          ),
        );
      }
    });

    setFilteredData(filteredDataResult);
  }, [checkboxFilters, dashboardData]);

  const getStatusLabel = (status: EntityStatus) => {
    return i18n.t(`Status.${EntityStatus[status].toLowerCase()}`);
  };

  const dashboardTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.subject'), dataKey: 'subjectName' },
    { label: i18n.t('Entity.site'), dataKey: 'siteName' },
    { label: i18n.t('Entity.deviceId'), dataKey: 'deviceIds' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (row: DashboardRow) => getStatusLabel(row.status),
    },
  ];

  const dashboardTableCellRenderer = (dataKey: string, row: DashboardRow) => {
    if (dataKey === 'subjectName') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            type="$bodyBase.default"
            onPress={() => navigateToSubject(row.subjectKey, row.subjectName)}
            href=""
          >
            {row.subjectName}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'siteName') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            type="$bodyBase.default"
            onPress={() => navigateToSite(row.siteKey, row.siteName)}
            href=""
          >
            {row.siteName}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(row.status)}
        </Table.TextCell>
      );
    } else if (dataKey === 'deviceIds') {
      return (
        <Table.TextCell key={dataKey}>
          {row.deviceIds.length > 0 ? row.deviceIds.join(', ') : ''}
        </Table.TextCell>
      );
    } else {
      return (
        <Table.TextCell key={dataKey}>{row[dataKey] || ''}</Table.TextCell>
      );
    }
  };

  // change handler for study dropdown - this will clear all selected subjects and filters
  const handleStudyChange = (item: SearchableDropdownItem) => {
    const index = studies.data?.findIndex((study) => study.key === item.key);

    if (index !== undefined && index !== -1) {
      setSelectedStudyIndex(index);
      setSelectedSubjectKeys([]);
    }
  };

  const onExportButtonPress = async (
    downloadMode: (typeof DOWNLOAD_MODE)[keyof typeof DOWNLOAD_MODE],
  ) => {
    if (!capabilities?.canGetStudyData) {
      log.warn('Export blocked: missing canGetStudyData capability');
      return;
    }

    if (
      !selectedStudyKey ||
      !selectedStudyName ||
      (downloadMode === DOWNLOAD_MODE.SUBJECTS &&
        selectedSubjectKeys.length === 0)
    ) {
      return;
    }

    // Open modal when starting download
    setIsDownloadModalOpen(true);

    try {
      await downloadStudyData({
        downloadMode,
        studyKey: selectedStudyKey,
        studyName: selectedStudyName,
        subjectKeys: selectedSubjectKeys,
      });
    } catch (error) {
      log.error('download failed:', error);
    }
  };

  const handleCloseModal = () => {
    setIsDownloadModalOpen(false);
    resetDownload();
  };

  return (
    <VStack height={'100%'}>
      {studies.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="dashboard-list-loading-spinner" />
        </VStack>
      ) : null}

      {studies.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Dashboard.error_title')}
          body={i18n.t('Page_Dashboard.error_default')}
        />
      ) : null}

      {studies.isSuccess ? (
        <VStack gap={'$4'} height={'100%'}>
          {!studies.data.length ? (
            <Highlight
              variant="informational"
              iconStart={
                <IconBannersInfo
                  color="$neutral.0"
                  accentColor="$support.informational.default"
                />
              }
              title={i18n.t('Page_Dashboard.no_studies_title')}
              body={i18n.t('Page_Dashboard.no_studies_description')}
            />
          ) : null}
          <Card
            title={i18n.t('Page_Dashboard.title')}
            titleAriaLevel={1}
            titleTypeToken="$h4.strong"
          >
            <DynamicTable
              description={i18n.t('Page_Dashboard.table_description')}
              isLoading={selectedStudyKey !== undefined && studyData.isPending}
              hasLoadingError={studyData.isError}
              columnData={dashboardTableColumnData}
              rowData={filteredData}
              cellRenderer={dashboardTableCellRenderer}
              enableFiltering={true}
              checkboxFilters={checkboxFilters}
              onFiltersChange={setCheckboxFilters}
              onFiltersReset={() => {
                setCheckboxFilters((prevFilters) =>
                  prevFilters.map((filter) => ({
                    ...filter,
                    items: filter.items.map((item) => ({
                      ...item,
                      value: false,
                    })),
                  })),
                );
              }}
              enableSelection={capabilities?.canGetStudyData}
              selectedRowKeys={selectedSubjectKeys}
              onSelectionChange={setSelectedSubjectKeys}
              rowKeyField="subjectKey"
              selectionActions={
                capabilities?.canGetStudyData ? (
                  <Button
                    variant="secondary"
                    size="medium"
                    label={i18n.t('Common.exportSelected')}
                    iconStart={<IconDownload />}
                    onPress={() => onExportButtonPress(DOWNLOAD_MODE.SUBJECTS)}
                    disabled={selectedSubjectKeys.length === 0 || isDownloading}
                    testID="dashboard-export-selected-button"
                  />
                ) : null
              }
              showCard={false}
              toolbarLeftContent={
                <VStack flexGrow={1}>
                  <SearchableDropdown
                    label={i18n.t('Entity.study')}
                    items={studies.data.map((study) => ({
                      key: study.key,
                      value: study.name,
                    }))}
                    selectedItem={
                      selectedStudyKey
                        ? {
                            key: selectedStudyKey,
                            value: selectedStudyName || '',
                          }
                        : undefined
                    }
                    onChange={handleStudyChange}
                    size="medium"
                    disabled={studies.isPending}
                    testID="dashboard-study-dropdown"
                  />
                </VStack>
              }
              toolbarRightContent={
                capabilities?.canGetStudyData ? (
                  <Button
                    variant="secondary"
                    size="medium"
                    label={i18n.t('Common.exportStudy')}
                    iconStart={<IconDownload />}
                    onPress={() => onExportButtonPress(DOWNLOAD_MODE.STUDY)}
                    disabled={
                      isDownloading ||
                      studies.isPending ||
                      !selectedStudyKey ||
                      !selectedStudyName
                    }
                    testID="dashboard-export-study-button"
                  />
                ) : null
              }
            />
          </Card>
        </VStack>
      ) : null}

      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={handleCloseModal}
        studyName={selectedStudyName || ''}
        {...downloadState}
      />
    </VStack>
  );
}
